/**
 * Polls — active polls vote inline (select → submit → results bars with %);
 * closed polls show results. Ported from design/source/screens-poll-event.jsx.
 */
import { router } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import {
  CardSkeletons, goBack, PollOptionRow, PollResultRow, ScopeBadge, SubTabs,
} from '@/features/community/bits';
import { fetchPolls, pct, useFetch, votePoll, withVote, type Poll } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Pill, Screen, TopBar } from '@/ui';

export default function PollsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [tab, setTab] = useState<'active' | 'closed'>('active');
  const { data, loading, error, refetch, mutate } = useFetch(fetchPolls, []);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [voteErr, setVoteErr] = useState<string | null>(null);

  const list = (data ?? []).filter((p) => (tab === 'active' ? p.status === 'active' : p.status === 'closed'));

  const submitVote = async (poll: Poll) => {
    const optionId = choices[poll.id];
    if (optionId == null || busyId) return;
    const idx = poll.options.findIndex((o) => o.id === optionId);
    const prev = data;
    setVoteErr(null);
    setBusyId(poll.id);
    mutate((ps) => (ps ?? []).map((p) => (p.id === poll.id ? withVote(p, optionId) : p)));
    try {
      await votePoll(poll.id, optionId, idx);
    } catch (e) {
      mutate(() => prev);
      // Eligibility 403s carry the bye-law reason — show it over the generic line.
      const err = e as { error?: string; citation?: string };
      setVoteErr(err?.error
        ? (err.citation ? `${err.error} (${err.citation})` : err.error)
        : t('poll_vote_error'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('ct_tab_polls')} onBack={goBack} />
      <SubTabs tabs={[['active', t('poll_active')], ['closed', t('poll_closed')]] as const} value={tab} onChange={setTab} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {loading ? (
          <CardSkeletons />
        ) : error ? (
          <EmptyState
            title={t('ct_polls_err_title')}
            body={t('poll_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            title={tab === 'active' ? t('ct_polls_empty_title') : t('poll_empty_closed_title')}
            body={
              tab === 'active'
                ? t('ct_polls_empty_body')
                : t('poll_empty_closed_body')
            }
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {voteErr ? <AppText variant="bodySm" tone="danger">{voteErr}</AppText> : null}
            {list.map((p) => (
              <PollCard
                key={p.id}
                poll={p}
                choice={choices[p.id] ?? null}
                onChoose={(optionId) => setChoices((c) => ({ ...c, [p.id]: optionId }))}
                onSubmit={() => void submitVote(p)}
                busy={busyId === p.id}
              />
            ))}
          </View>
        )}
      </Screen>
    </View>
  );
}

function PollCard({ poll, choice, onChoose, onSubmit, busy }: {
  poll: Poll; choice: string | null; onChoose: (optionId: string) => void; onSubmit: () => void; busy: boolean;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const voted = poll.myOptionId != null || poll.status === 'closed';
  const sum = poll.options.reduce((s, o) => s + o.votes, 0);

  return (
    <Card onPress={() => router.push(`/polls/${poll.id}`)}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
        <ScopeBadge scope={poll.scope} />
        {poll.anonymous ? <Pill status="pending" label={t('ct_anonymous')} /> : null}
      </View>
      <AppText variant="headingSm" weight="500" style={{ marginTop: 10 }}>{poll.question}</AppText>

      <View style={{ marginTop: space.s3, gap: space.s2 }}>
        {voted ? (
          poll.options.map((o) => (
            <PollResultRow key={o.id} label={o.label} pct={pct(o.votes, sum)} mine={poll.myOptionId === o.id} />
          ))
        ) : (
          <>
            {poll.options.map((o) => (
              <PollOptionRow key={o.id} label={o.label} selected={choice === o.id} onPress={() => onChoose(o.id)} />
            ))}
            <Button
              title={t('poll_submit_vote')}
              disabled={choice == null}
              loading={busy}
              onPress={onSubmit}
              style={{ marginTop: space.s1 }}
            />
          </>
        )}
      </View>

      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: space.s3 }}>
        <AppText variant="bodySm" tone="tertiary">{t('ct_voted', { n: poll.votes, total: poll.total })}</AppText>
        <AppText
          variant="bodySm"
          weight="500"
          style={{ color: poll.status === 'active' ? colors.primary500 : colors.textTertiary }}
        >
          {poll.deadline}
        </AppText>
      </View>
      {poll.total > 0 ? (
        <View style={{ marginTop: space.s2, height: 3, borderRadius: radius.full, backgroundColor: colors.canvas2, overflow: 'hidden' }}>
          <View
            style={{
              width: `${Math.min(100, pct(poll.votes, poll.total))}%`,
              height: '100%',
              backgroundColor: colors.primary400,
            }}
          />
        </View>
      ) : null}
    </Card>
  );
}
